import {ApolloError, useQuery} from '@apollo/client';
import {useMemo} from 'react';

import type {
  GetScientificModelRegistry,
  GetScientificModelRegistryVariables,
} from './__generated/GetScientificModelRegistry';
import {
  getAvailableModelsByCategory,
  indexModelsByExternalReference,
  MODEL_CATEGORY,
  ScientificModelNode,
  ScientificModelsByExternalReference,
} from './ScientificModelMetadata';
import {GET_SCIENTIFIC_MODEL_REGISTRY_QUERY} from './ScientificModelsQueries';

export interface ScientificModelRegistry {
  availableModels: ReadonlyArray<ScientificModelNode>;
  availableGenerativeModels: ReadonlyArray<ScientificModelNode>;
  availableModelsByExternalReference: ScientificModelsByExternalReference;
  availablePropertyPredictionModels: ReadonlyArray<ScientificModelNode>;
  availableStructurePredictionModels: ReadonlyArray<ScientificModelNode>;
  catalog: ReadonlyArray<ScientificModelNode>;
  catalogByExternalReference: ScientificModelsByExternalReference;
}

export type ScientificModelRegistryResult =
  | {type: 'loading'}
  | {type: 'error'; error: ApolloError}
  | {type: 'ready'; registry: ScientificModelRegistry};

export function buildScientificModelRegistry(
  availableModels: ReadonlyArray<ScientificModelNode>,
  catalog: ReadonlyArray<ScientificModelNode>
): ScientificModelRegistry {
  const submittableModels = availableModels.filter((model) => model.isSubmittableInUi);
  return {
    availableModels: submittableModels,
    availableGenerativeModels: getAvailableModelsByCategory(submittableModels, MODEL_CATEGORY.GENERATIVE),
    availableModelsByExternalReference: indexModelsByExternalReference(submittableModels),
    availablePropertyPredictionModels: getAvailableModelsByCategory(
      submittableModels,
      MODEL_CATEGORY.PROPERTY_PREDICTION
    ),
    availableStructurePredictionModels: getAvailableModelsByCategory(
      submittableModels,
      MODEL_CATEGORY.STRUCTURE_PREDICTION
    ),
    catalog,
    catalogByExternalReference: indexModelsByExternalReference(catalog),
  };
}

export function useScientificModelRegistry(): ScientificModelRegistryResult {
  const {data, loading, error} = useQuery<GetScientificModelRegistry, GetScientificModelRegistryVariables>(
    GET_SCIENTIFIC_MODEL_REGISTRY_QUERY
  );
  const registry = useMemo(
    () =>
      data == null
        ? null
        : buildScientificModelRegistry(data.availableScientificModels, data.scientificModelCatalog),
    [data]
  );

  if (error != null) {
    return {type: 'error', error};
  }
  if (loading || registry == null) {
    return {type: 'loading'};
  }
  return {type: 'ready', registry};
}

export function useScientificModelDisplayName(externalReference: string | undefined): string | undefined {
  const {data} = useQuery<GetScientificModelRegistry, GetScientificModelRegistryVariables>(
    GET_SCIENTIFIC_MODEL_REGISTRY_QUERY,
    {skip: externalReference == null}
  );
  return data?.scientificModelCatalog.find((model) => model.externalReference === externalReference)
    ?.displayName;
}
